/**
 * The chat widget: state and render.
 *
 * Two components. `ChatWidget` resolves WHERE the configuration comes from —
 * the server (GET /chat/config), the props, or nothing at all when the server
 * says this page must not show an assistant — and renders nothing until that
 * is settled. `ChatPanel` is the widget proper, mounted only once, with its
 * greeting and chips initialised from the RESOLVED configuration; mounting it
 * earlier would seed the transcript with the snippet's greeting and paint the
 * server's over it a moment later.
 *
 * The pieces this file used to hold live beside it: config.ts (resolution),
 * storage.ts (IndexedDB), useHistoryPolling.ts (operator replies), theme.ts
 * (colour + styles), icons.tsx, DetailsForm.tsx + details.ts (the one details
 * form, ADR-110 Am.6), lead.ts (lead capture), actions.ts (booking steps,
 * ADR-110), FeedbackForm.tsx
 * + feedback.ts ("Send feedback", for the Ema Answers team), humanCheck.ts +
 * HumanCheckFrame.tsx (the human check, ADR-123), markdown.ts (the one
 * HTML-producing module, with tests) and strings.ts.
 */
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useId,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { stripMarkdown } from './markdown';
import { AssistantMarkdown } from './AssistantMarkdown';
import { findSection, sectionOnThisPage, showSection } from './sectionLink';
import { fill, stringsFor } from './strings';
import { LanguageMenu } from './LanguageMenu';
import {
  choiceNotice,
  choosable,
  drawnLanguage,
  ENDONYMS,
  languageCode,
  readChoice,
  writeChoice,
  type Choosable,
} from './languages';
import { fetchServerConfig, OPERATOR_PRIVACY_URL, resolveConfig, type BlockedCode, type ResolvedConfig } from './config';
import { loadChat, saveChat, clearChat, isFresh } from './storage';
import { useHistoryPolling } from './useHistoryPolling';
import { onCommand } from './commands';
import { buildStyles, sendButtonStyle, srOnlyStyle, KEYFRAMES_STYLE_ID, KEYFRAMES_CSS } from './theme';
import {
  ChatIcon,
  CloseIcon,
  FeedbackIcon,
  InfoIcon,
  PlusIcon,
  CheckIcon,
  ExpandIcon,
  CollapseIcon,
  SendIcon,
  RetryIcon,
  CalendarIcon,
  PersonIcon,
  GlobeIcon,
} from './icons';
import { FeedbackForm } from './FeedbackForm';
import { feedbackPage, feedbackRequest, submitFeedback, type FeedbackInput } from './feedback';
import { DetailsForm, type Refusal } from './DetailsForm';
import { FindBookingForm } from './FindBookingForm';
import { submitLead, summariseConversation, type LeadInput, type LeadOutcome } from './lead';
import { refusalSentence } from './fieldMessages';
import { counterFrom, defaultPhoneCountry, wholePaste } from './fields';
import { streamMessage } from './stream';
import { isHumanCheckRefusal } from './humanCheck';
import { classifyFailure, parseStatusReport, sendReport, TurnError, type TurnFailure } from './statusReport';
import { HumanCheckError, useHumanCheck } from './HumanCheckFrame';
import {
  bookingChoices,
  bookingDetailsLine,
  formatSlotLabel,
  isBookingControl,
  lookupLine,
  parseActions,
  type BookingChoiceKind,
  type BookingDetails,
  type BookingLookup,
  type ChatAction,
} from './actions';
import type { ChatWidgetProps, Message } from './types';
import { version } from './version';

export type { ChatWidgetProps } from './types';

/**
 * A /chat/message call that has not answered by then is abandoned and shown
 * as a connection failure with a retry. The backend's own Lambda timeout is
 * 30 s; without this the typing dots ran for as long as the browser allowed.
 */
const MESSAGE_TIMEOUT_MS = 35_000;

/**
 * The longest message a turn may carry: POST /chat/message refuses more
 * ("Message too long (max 2000 characters)"). The box stops typing there, and
 * a paste past it is kept whole and named rather than cut (1.31.0).
 */
const MESSAGE_MAX = 2000;

/**
 * How long each language's "type a message" stays in the empty input (1.18.0).
 * Long enough to read and to notice it changed, slow enough not to flicker at
 * the edge of a visitor's eye while they think.
 */
const PLACEHOLDER_ROTATE_MS = 2_800;

/**
 * Canned backend replies arrive with a `code` beside the English `reply`; the
 * visitor gets the widget's own copy in their language instead (review S10).
 * A reply without a code is a real answer and is shown as sent.
 */
const CANNED_REPLY_KEYS = {
  no_content: 'noContent',
  over_cap: 'overCap',
  unavailable: 'unavailable',
  refused: 'refused',
} as const;

type PanelSize = 'default' | 'expanded';

type Chip = { kind: 'text'; text: string } | { kind: 'link'; label: string; url: string };

/**
 * The two standing calls to action, in the order they are drawn. Leaving your
 * details comes first: it is the one every business has, and the one a visitor
 * who cannot find a time still wants.
 */
type PrimaryAction = 'lead' | 'booking';

/**
 * One turn as it goes on the wire. `message` is what the visitor typed or
 * tapped — for a booking step, the value the server reads (a slot's instant,
 * `yes`) rather than the label in their bubble — and a details form adds
 * `details` (ADR-110). A retry resends exactly this.
 */
interface OutgoingTurn {
  message: string;
  details?: BookingDetails;
  /** The find-booking form's values (ADR-110 Amendment 10). */
  lookup?: BookingLookup;
}

/** The test hook on each booking chip. */
const CHOICE_PWE: Record<BookingChoiceKind, string> = {
  option: 'booking-option',
  slot: 'booking-slot',
  yes: 'booking-confirm-yes',
  no: 'booking-confirm-no',
};

/** Whether a link chip points at the page's own site, which then opens in place. */
function isSameOrigin(url: string): boolean {
  try {
    return typeof location !== 'undefined' && new URL(url).origin === location.origin;
  } catch {
    return false;
  }
}

function explainBlocked(code: BlockedCode, tenantId: string, apiUrl: string): void {
  const origin = typeof location !== 'undefined' ? location.origin : 'this site';
  switch (code) {
    case 'tenant_unknown':
      console.error(
        `[PweChat] Tenant ${JSON.stringify(tenantId)} is unknown to ${apiUrl} — the widget was not shown. ` +
          'Check the tenantId in your snippet against Setup → Install in the Evrella console.',
      );
      return;
    case 'origin_not_allowed':
      console.error(
        `[PweChat] ${origin} is not on the list of sites this assistant may run on — the widget was not shown. ` +
          'The list follows the website saved under Knowledge › From your website in the Evrella console (that site and its subdomains).',
      );
      return;
    case 'assistant_unavailable':
      console.info('[PweChat] This assistant is not available right now; the widget was not shown.');
      return;
  }
}

export function ChatWidget(props: ChatWidgetProps) {
  const { apiUrl, tenantId, fetchConfig = true } = props;

  // The props-only resolution, kept live for surfaces that drive the
  // configuration themselves (the console's Personalise preview re-renders
  // with every keystroke in the colour field). Objects in the deps are
  // reduced to primitives so a fresh `offset={{...}}` literal does not force
  // a resolve — and a console.warn for a bad colour — on every render.
  const fromProps = useMemo(
    () => (fetchConfig ? null : resolveConfig(props, null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchConfig,
      props.language,
      props.businessName,
      props.greeting,
      props.brandColor,
      props.avatarUrl,
      props.privacyUrl,
      props.feedbackApiUrl,
      props.launcherSelector,
      props.linkChipLabel,
      props.linkChipUrl,
      props.position,
      props.leadCaptureEnabled,
      props.bookingEnabled,
      JSON.stringify(props.leadForm ?? null),
      props.offset?.bottom,
      props.offset?.side,
      JSON.stringify(props.defaultSuggestions ?? []),
    ],
  );

  const [fromServer, setFromServer] = useState<ResolvedConfig | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!fetchConfig) return;
    const ctrl = new AbortController();
    let active = true;
    fetchServerConfig(apiUrl, tenantId, { signal: ctrl.signal }).then((outcome) => {
      if (!active) return;
      if (outcome.kind === 'ok') {
        if (!outcome.config.chatbotEnabled) {
          console.info('[PweChat] The assistant is switched off in the Evrella console; the widget was not shown.');
          setHidden(true);
          return;
        }
        setFromServer(resolveConfig(props, outcome.config));
        return;
      }
      if (outcome.kind === 'fallback') {
        console.warn(
          `[PweChat] Could not load the assistant configuration from ${apiUrl} (${outcome.reason}); using the snippet options.`,
        );
        setFromServer(resolveConfig(props, null));
        return;
      }
      explainBlocked(outcome.code, tenantId, apiUrl);
      setHidden(true);
    });
    return () => {
      active = false;
      ctrl.abort();
    };
    // The snippet's props do not change on a real site; the console preview
    // passes fetchConfig={false} and takes the memo above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, tenantId, fetchConfig]);

  if (hidden) return null;
  const config = fetchConfig ? fromServer : fromProps;
  if (!config) return null;
  return <ChatPanel props={props} config={config} />;
}

function ChatPanel({ props, config }: { props: ChatWidgetProps; config: ResolvedConfig }) {
  const {
    apiUrl,
    tenantId,
    externalTrigger = false,
    toggleEvent = 'assistbar:toggle-chat',
    enableCart = false,
    defaultOpen = false,
    // 🚨 THE PANEL OPENS EXPANDED SINCE 1.26.0. It used to open as the
    // 380×520 card and leave the bigger size behind a header control most
    // visitors never pressed; a conversation is easier to read wide, and the
    // control still collapses it. Pass `defaultExpanded: false` for the card.
    defaultExpanded = true,
    persist = true,
  } = props;
  const {
    strings: pageStrings,
    language,
    businessName,
    greeting,
    brandColor,
    avatarUrl,
    position,
    offset,
    privacyUrl,
    feedbackApiUrl,
    launcherSelector,
    linkChip,
    leadCaptureEnabled,
    bookingEnabled,
    leadForm,
    launcherLabel,
    streamUrl,
    languageChoice,
  } = config;
  // ── The conversation's language (1.32.0, ADR-128 Am.4, ADR-110 Am.11) ──
  // `language` is the PAGE's (config.ts): what every request still sends as
  // the page's evidence, and what the widget speaks before anyone writes.
  // From the first reply on it speaks the CONVERSATION's — the `language` the
  // backend's one decider put on the reply — and above both stands the
  // visitor's CHOICE in the header's control, which sticks. `ui` is the copy
  // in whichever of the three is in force, so every word the widget draws —
  // chips, forms, the day and time labels of a booking — is in one language
  // with the reply beside it. See languages.ts.
  const [chosenLanguage, setChosenLanguage] = useState<Choosable | ''>('');
  const [conversationLanguage, setConversationLanguage] = useState<Choosable | ''>('');
  const [languageOpen, setLanguageOpen] = useState(false);
  const uiLanguage = drawnLanguage(languageChoice ? chosenLanguage : '', conversationLanguage, language);
  const ui = useMemo(
    () => (uiLanguage === language ? pageStrings : stringsFor(uiLanguage)),
    [uiLanguage, language, pageStrings],
  );
  // Copy a snippet may still override; the localised default otherwise.
  const errorMessage = props.errorMessage || ui.errorMessage;
  const originBlockedMessage = props.originBlockedMessage || ui.originBlockedMessage;
  const subtitle = props.subtitle || ui.subtitle;
  const privacyNotice = props.privacyNotice || ui.privacyNotice;
  const privacyLinkLabel = props.privacyLinkLabel || ui.privacyLinkLabel;
  // The page opens the chat from its own control — a `window` event
  // (`externalTrigger`) or its own button (`launcherSelector`) — so the widget
  // draws no launcher of its own.
  const hostLaunched = externalTrigger || Boolean(launcherSelector);
  // "Report this issue" (1.33.0): snippet-only, never from the server.
  const statusReportKey = JSON.stringify(props.statusReport ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusReport = useMemo(() => parseStatusReport(props.statusReport), [statusReportKey]);

  const nextIdRef = useRef(1);
  const newId = () => `m${nextIdRef.current++}`;

  const [open, setOpen] = useState(defaultOpen);
  /**
   * The panel has been open at least once on this page.
   *
   * 🚨 NOTHING IS READ FROM OR WRITTEN TO THE VISITOR'S DEVICE BEFORE THIS IS
   * TRUE. Until 1.25.0 the widget restored the conversation and saved the
   * opening state the moment it mounted, so merely LOADING a page that carries
   * the snippet left an IndexedDB record behind and read back a `sessionStorage`
   * pass, whatever the visitor did next. That is storage a closed launcher does
   * not need, and it is why the page that sells Ema would not load her at all
   * until a cookie banner said yes.
   *
   * Opening the chat is the visitor asking for the chat. From that moment the
   * transcript and the anti-abuse pass are storage strictly necessary for a
   * service they explicitly requested — PECR reg 6(4), which is exactly what
   * our published cookie policy has always said this widget relies on — so no
   * banner stands between a visitor and an answer, and a visitor who never
   * touches the launcher leaves with nothing stored.
   *
   * `defaultOpen` starts here true: a panel that is open on screen is open,
   * whoever opened it.
   */
  const [everOpen, setEverOpen] = useState(defaultOpen);
  /**
   * `everOpen` latches ONE RENDER LATE — it is set from an effect — and that
   * one render matters: the effect that warms the human check up runs in the
   * same commit as the open, and a pass not yet read out of `sessionStorage`
   * looks like no pass at all, so a returning visitor would sit through a
   * fresh challenge on every open. Or-ing `open` in makes the render that
   * opens the panel already count. Closing never takes it away: closing the
   * chat does not un-ask for the chat, and a transcript that vanished on close
   * would be a worse surprise than one kept for the day the cookie policy
   * describes.
   */
  const engaged = everOpen || open;
  const [panelSize, setPanelSize] = useState<PanelSize>(defaultExpanded ? 'expanded' : 'default');
  const [messages, setMessages] = useState<Message[]>([{ id: 'greeting', role: 'assistant', content: greeting }]);
  const [input, setInput] = useState('');
  const composerId = useId();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(config.suggestions);
  const [restored, setRestored] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [lastFailed, setLastFailed] = useState<OutgoingTurn | null>(null);
  // Why the last turn failed, and where its report stands (1.33.0, ADR-131
  // Am.1). Drawn only beside "Try again", and only when the site passed
  // `statusReport`; null after a failed human check, which is not an outage.
  const [lastFailure, setLastFailure] = useState<TurnFailure | null>(null);
  const [reportState, setReportState] = useState<'idle' | 'sending' | 'done' | 'failed'>('idle');
  const [confirmClear, setConfirmClear] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  // Message id currently being revealed with the typewriter effect, plus how
  // many characters of it are visible.
  const [reveal, setReveal] = useState<{ id: string; chars: number } | null>(null);
  // Polling stops for this conversation: 403/404 (not ours any more) or the
  // operator closed/archived it. A new chat starts it again.
  const [pollStopped, setPollStopped] = useState(false);
  // Epoch ms of the visitor's last send / open / return to the tab: the
  // polling schedule goes hot again from here.
  const [activityAt, setActivityAt] = useState(() => Date.now());
  // Lead capture.
  const [leadOpen, setLeadOpen] = useState(false);
  /** The header avatar failed to load (a client site's CSP, a dead address): draw the mark instead. */
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadDisabled, setLeadDisabled] = useState(false);
  // An appointment taken on this conversation (1.18.0). Persisted like
  // `leadSubmitted`: the two standing chips each retire on their OWN
  // completion, and a reload must not offer a visitor a second appointment
  // they have already made.
  const [bookingTaken, setBookingTaken] = useState(false);
  // The link chip steps aside with the suggestions (the ×) until the next reply.
  const [linkChipHidden, setLinkChipHidden] = useState(false);
  // Booking steps (ADR-110): the latest assistant turn's actions, drawn below
  // it and cleared by the next send. Never persisted — after a reload they are
  // gone and typing carries on, because the server holds the booking's state.
  const [actions, setActions] = useState<ChatAction[]>([]);
  // "Send feedback" (feedback.ts): the form stands in for the conversation
  // while it is open. Never persisted; closing the panel closes it too.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // The info panel behind the header's ⓘ (1.19.0): the version, the privacy
  // notice and the way to tell us about the assistant. One at a time with the
  // feedback form, and never persisted.
  const [infoOpen, setInfoOpen] = useState(false);
  // The human check (ADR-123): a pass for this tenant, and the frame the
  // challenge runs in. Configured by the server; learnt from a refusal when
  // this surface mounted without the server's configuration.
  const { check: humanCheck, frame: humanCheckFrame } = useHumanCheck({
    apiUrl,
    tenantId,
    config: config.humanCheck,
    language,
    persist,
    engaged,
    title: ui.humanCheckTitle,
    visible: open,
  });

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = sessionId;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const feedbackButtonRef = useRef<HTMLButtonElement>(null);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const infoPanelRef = useRef<HTMLDivElement>(null);
  const wasFeedbackOpenRef = useRef(false);
  // The page's own button that last opened the panel: focus goes back to it.
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenAdminIdsRef = useRef<Set<string>>(new Set());
  const lastSkRef = useRef<string | null>(null);

  const reducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** A finger rather than a mouse: what tells a phone from a short pane. */
  const coarsePointer =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;

  const bumpActivity = useCallback(() => setActivityAt(Date.now()), []);

  useEffect(() => {
    if (open) setEverOpen(true);
  }, [open]);

  // ── Persistence ──────────────────────────────────────────────
  useEffect(() => {
    // The closed launcher touches no storage at all — see `engaged` above.
    if (!engaged) return;
    if (!persist) {
      // Ephemeral (preview) mode: always start from the CURRENT config.
      setRestored(true);
      return;
    }
    let active = true;
    loadChat(tenantId).then((record) => {
      if (!active) return;
      // The read now starts when the panel OPENS rather than at mount, so for
      // the first time there is a window — however short — in which the
      // visitor can type before the disk answers. A restore that landed then
      // would replace what they just wrote with what they wrote yesterday.
      if (messagesRef.current.length > 1 || sessionIdRef.current) {
        setRestored(true);
        return;
      }
      // loadChat has already checked the shape and the tenant; a stale record
      // is left alone and overwritten by the first save.
      if (record && isFresh(record) && record.messages.length > 0) {
        // The first bubble is always the greeting (initial state, and "new
        // chat" resets to it). Show the greeting the operator has NOW, not
        // the one saved up to 24 h ago — the README promises a console change
        // reaches every site on its next page load, returning visitors too.
        const [first, ...rest] = record.messages;
        const current = first.role === 'assistant' && !first.error ? { ...first, content: greeting } : first;
        setMessages([current, ...rest].map((m) => ({ ...m, id: newId() })));
        setSuggestions(record.suggestions.length > 0 ? record.suggestions : config.suggestions);
        if (record.sessionId) setSessionId(record.sessionId);
        seenAdminIdsRef.current = new Set(record.adminIds ?? []);
        lastSkRef.current = record.lastSk ?? null;
        setLeadSubmitted(record.leadSubmitted === true);
        setBookingTaken(record.bookingTaken === true);
        setConversationLanguage(choosable(record.language));
      }
      setRestored(true);
    });
    return () => {
      active = false;
    };
    // `engaged` latches true once and never goes back, so this runs at most
    // twice: once doing nothing, once restoring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engaged]);

  // The language this visitor chose on this site before (1.32.0). Read when
  // the panel first opens and not a moment earlier — see `engaged` — and only
  // where the control is offered: a pinned assistant has nothing to choose.
  useEffect(() => {
    if (!engaged || !persist || !languageChoice) return;
    const stored = readChoice(tenantId);
    if (stored) setChosenLanguage(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engaged]);

  useEffect(() => {
    // `restored` cannot be true before the panel has been opened, so this
    // writes nothing on a page the visitor only scrolled past.
    if (!restored || !persist) return;
    saveChat(tenantId, {
      tenantId,
      messages: messages.map(({ role, content, error, language: lang }) => ({
        role,
        content,
        ...(error ? { error } : {}),
        ...(lang ? { language: lang } : {}),
      })),
      suggestions,
      updatedAt: Date.now(),
      ...(sessionId ? { sessionId } : {}),
      adminIds: Array.from(seenAdminIdsRef.current),
      ...(lastSkRef.current ? { lastSk: lastSkRef.current } : {}),
      ...(leadSubmitted ? { leadSubmitted } : {}),
      ...(bookingTaken ? { bookingTaken } : {}),
      ...(conversationLanguage ? { language: conversationLanguage } : {}),
    });
  }, [messages, suggestions, sessionId, restored, leadSubmitted, bookingTaken, conversationLanguage, persist, tenantId]);

  // ── Scrolling, focus, open/close ─────────────────────────────
  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // The latest turn's booking steps are drawn once it has settled: not while
  // a send is out, while the reply is still being typed out, or after a failure.
  const bookingSettled = actions.length > 0 && !loading && !reveal && !lastFailed;

  useEffect(() => {
    scrollToBottom();
  }, [messages, leadOpen, bookingSettled, scrollToBottom]);

  // The check starts when the panel opens rather than when the visitor sends:
  // by the first message it has usually finished, so `enforce` adds no wait,
  // and `log` also measures the visitors who open the chat and never write.
  useEffect(() => {
    if (!open || !humanCheck.mode() || humanCheck.current()) return;
    humanCheck.obtain().catch(() => undefined);
  }, [open, humanCheck]);

  useEffect(() => {
    if (!open) return;
    // 🚨 NOT ON A TOUCH DEVICE (1.29.0). iOS Safari gives a programmatic
    // focus() outside a tap no keyboard, and then IGNORES the visitor's tap on
    // the box, because the box is already focused: nothing changes focus, so
    // nothing raises the keyboard. Visitors reported a composer that "does
    // not open the keyboard" until they tapped something else first — the
    // something else blurred the box, and the next tap was a real focus. On a
    // phone the composer is focused by the visitor's finger and by nothing
    // else; a keyboard over the greeting was never wanted there anyway.
    if (!coarsePointer) inputRef.current?.focus();
    // Scroll to bottom when chat opens (e.g. after page refresh with restored history)
    const t = setTimeout(() => scrollToBottom(), 100);
    bumpActivity();
    return () => clearTimeout(t);
  }, [open, coarsePointer, scrollToBottom, bumpActivity]);

  // Back from the feedback form. The transcript was hidden, which drops its
  // scroll position, so land at its end — with the caret in the composer on a
  // desktop, and on the header button on a phone, where focusing the input
  // would throw the keyboard up over the conversation.
  useEffect(() => {
    if (wasFeedbackOpenRef.current && !feedbackOpen && open) {
      scrollToBottom(false);
      if (isMobile) feedbackButtonRef.current?.focus();
      else inputRef.current?.focus();
    }
    wasFeedbackOpenRef.current = feedbackOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackOpen]);

  /**
   * `fromInside` = the close came from the panel (its close button, Escape
   * with focus in it), so focus goes back to the launcher. A close from the
   * host page's own trigger leaves the host's focus where it is (review S13).
   */
  const closePanel = useCallback(
    (fromInside: boolean) => {
      setOpen(false);
      setPanelSize('default');
      setFeedbackOpen(false);
      setInfoOpen(false);
      setLanguageOpen(false);
      if (!fromInside) return;
      if (!hostLaunched) {
        setTimeout(() => launcherRef.current?.focus(), 0);
      } else if (lastTriggerRef.current?.isConnected) {
        // Back to the page's own button, when one opened it.
        const trigger = lastTriggerRef.current;
        setTimeout(() => trigger.focus(), 0);
      }
    },
    [hostLaunched],
  );

  // Escape is handled ON the panel, not on window: a host lightbox's Escape
  // used to close the chat too and yank focus to the corner.
  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && infoOpen) {
      // The innermost thing first: Escape under an open info panel closes the
      // panel, not the conversation behind it.
      e.stopPropagation();
      closeInfo();
      return;
    }
    if (e.key === 'Escape' && languageOpen) {
      // The language menu likewise (1.32.0).
      e.stopPropagation();
      closeLanguage();
      return;
    }
    if (e.key === 'Escape') {
      e.stopPropagation();
      closePanel(true);
    }
  };

  // A host page's own control, by event (`externalTrigger`): puglieseweb.com's
  // AssistBar dispatches `assistbar:toggle-chat`.
  useEffect(() => {
    if (!externalTrigger) return;
    const handler = () => setOpen((prev) => !prev);
    window.addEventListener(toggleEvent, handler);
    return () => window.removeEventListener(toggleEvent, handler);
  }, [externalTrigger, toggleEvent]);

  // PweChat.open() / close() / toggle() (commands.ts), whatever else opens the panel.
  useEffect(
    () => onCommand((command) => setOpen((prev) => (command === 'toggle' ? !prev : command === 'open'))),
    [],
  );

  // A host page's own button, by selector (`launcherSelector`). One listener on
  // the document, in the capture phase, rather than one on each element: a
  // site builder renders its buttons late, re-renders them and adds more, and
  // a host handler that stops propagation must not swallow the click. The
  // widget's own panel is never a trigger, whatever the selector matches —
  // "button" would otherwise shut it on every click inside it.
  useEffect(() => {
    if (!launcherSelector) return;
    try {
      if (!document.querySelector(launcherSelector)) {
        console.warn(
          `[PweChat] Nothing on this page matches launcherSelector ${JSON.stringify(launcherSelector)} yet; the chat opens when an element that matches it is clicked.`,
        );
      }
    } catch {
      console.error(
        `[PweChat] launcherSelector ${JSON.stringify(launcherSelector)} is not a valid CSS selector, so nothing on this page opens the chat. Fix it in the Evrella console (Setup → Install).`,
      );
      return;
    }
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('[data-pwe="panel"]')) return;
      const trigger = target.closest(launcherSelector);
      if (!(trigger instanceof HTMLElement)) return;
      // The client named this element as their chat button: it opens the chat
      // and does nothing else, even when it is a link.
      event.preventDefault();
      lastTriggerRef.current = trigger;
      setOpen((prev) => !prev);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [launcherSelector]);

  // The page's own buttons say whether the chat they control is open.
  useEffect(() => {
    if (!launcherSelector) return;
    try {
      document.querySelectorAll(launcherSelector).forEach((el) => el.setAttribute('aria-expanded', String(open)));
    } catch {
      // An unparseable selector was reported once, above.
    }
  }, [open, launcherSelector]);

  // Coming back to the tab restarts polling hot; nothing polls while hidden.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bumpActivity();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [bumpActivity]);

  // ── Typewriter reveal ────────────────────────────────────────
  useEffect(() => {
    if (!reveal) return;
    const msg = messages.find((m) => m.id === reveal.id);
    if (!msg || reveal.chars >= msg.content.length) {
      setReveal(null);
      return;
    }
    // ~24ms per tick, sized so even long replies finish in ~2.5s.
    const step = Math.max(2, Math.ceil(msg.content.length / 100));
    const t = setTimeout(() => {
      setReveal((r) => (r && r.id === reveal.id ? { ...r, chars: r.chars + step } : r));
      scrollToBottom(false);
    }, 24);
    return () => clearTimeout(t);
  }, [reveal, messages, scrollToBottom]);

  const startReveal = (id: string) => {
    if (!reducedMotion) setReveal({ id, chars: 0 });
  };

  const pushAssistant = (content: string, error = false, lang = '') => {
    const id = newId();
    setMessages((prev) => [
      ...prev,
      { id, role: 'assistant', content, ...(error ? { error: true } : {}), ...(lang ? { language: lang } : {}) },
    ]);
    setAnnouncement(stripMarkdown(content));
    return id;
  };

  /**
   * The server may hand back a different sessionId at any turn (an expired
   * or re-parented conversation gets a fresh one). Adopt it every time; the
   * poll cursor and the seen operator ids belonged to the old conversation.
   */
  const adoptSession = (id: unknown) => {
    if (typeof id !== 'string' || !id) return;
    if (id !== sessionIdRef.current) {
      seenAdminIdsRef.current = new Set();
      lastSkRef.current = null;
      setPollStopped(false);
      setSessionId(id);
    }
  };

  // ── Send / retry ─────────────────────────────────────────────

  /** The assistant bubble a stream is writing into, while one is. */
  const streamIdRef = useRef<string | null>(null);
  const [streaming, setStreaming] = useState(false);

  /**
   * A finished turn — the REST body, or the stream's `done` (ADR-103 §2).
   * Both carry the same fields. When a stream wrote a bubble, its text is
   * REPLACED with `reply`: the server checks the whole reply after the
   * stream (a link it drops, a comment it strips, a leak it refuses), and
   * what it settled on is the reply, not what was previewed.
   */
  const applyReply = (data: Record<string, unknown>) => {
    adoptSession(data.sessionId);

    const code = typeof data.code === 'string' ? (data.code as keyof typeof CANNED_REPLY_KEYS) : null;
    const cannedKey = code ? CANNED_REPLY_KEYS[code] : undefined;
    const replyText = cannedKey ? ui[cannedKey] : typeof data.reply === 'string' ? data.reply : '';
    if (!replyText) throw new Error('empty reply');

    // The conversation's language, as the backend's one decider put it on the
    // reply (ADR-110 Am.11). From here on every word the widget draws follows
    // it. Only the five the widget speaks are adopted: a reply in Polish is
    // still shown, and the widget stays in the language it was in around it.
    // A canned reply carries none and is the widget's own copy, already in
    // the language in force.
    const replyLanguage = choosable(data.language);
    if (replyLanguage) setConversationLanguage(replyLanguage);
    const bubbleLanguage = cannedKey ? uiLanguage : replyLanguage;

    const streamed = streamIdRef.current;
    streamIdRef.current = null;
    if (streamed) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === streamed ? { ...m, content: replyText, ...(bubbleLanguage ? { language: bubbleLanguage } : {}) } : m,
        ),
      );
      setAnnouncement(stripMarkdown(replyText));
    } else {
      const replyId = pushAssistant(replyText, false, bubbleLanguage);
      startReveal(replyId);
    }
    setLastFailed(null);
    setLinkChipHidden(false);

    setSuggestions(Array.isArray(data.suggestions) ? data.suggestions.filter((s: unknown) => typeof s === 'string') : []);

    // The booking steps belong to THIS turn (ADR-110): a reply without any
    // clears the last turn's. Unknown or malformed actions are dropped, and
    // the reply text above stands on its own.
    const turnActions = parseActions(data.actions);
    setActions(turnActions);

    // An appointment was taken: that chip has done its job and goes (1.18.0).
    // So does the lead chip — an appointment IS a lead (ADR-117: the booking
    // writes the same LEAD# row, with the details the visitor just gave), so
    // "Leave your details" would be asking a second time for what the
    // business already has. 1.18.0 kept it on the reasoning that someone who
    // booked might still want a call back; they can simply say so, and the
    // chip standing there after a confirmed booking read as an unfinished
    // step (owner, 2026-09-15).
    if (turnActions.some((a) => a.type === 'booked')) {
      setBookingTaken(true);
      setLeadSubmitted(true);
    }
    // A booking the visitor cancelled leaves them free to book again, so the
    // chip comes back (ADR-110 Amendment 10).
    if (turnActions.some((a) => a.type === 'cancelled')) {
      setBookingTaken(false);
    }

    // Cart sync (LEGACY, opt-in)
    if (enableCart && data.cartId) {
      window.dispatchEvent(new CustomEvent('basket:sync', { detail: { cartId: data.cartId } }));
    }
  };

  /**
   * 403 with one of our codes: this page is not on the tenant's allowed
   * site list, or the assistant is off. Retrying cannot fix either, so say
   * so plainly and stop offering a retry — otherwise a misconfigured
   * install just looks like an outage, and the person who can fix it never
   * learns why. Returns true when it was one of those.
   */
  const applyBlocked = (status: number, detail: Record<string, unknown> | null): boolean => {
    if (status !== 403 || !detail) return false;
    if (detail.code === 'origin_not_allowed') {
      setLastFailed(null);
      pushAssistant(originBlockedMessage, true);
      return true;
    }
    if (detail.code === 'assistant_unavailable') {
      setLastFailed(null);
      pushAssistant(ui.unavailable, true);
      return true;
    }
    return false;
  };

  /** A streamed delta: open the bubble on the first, append to it after. */
  const appendDelta = (text: string) => {
    const id = streamIdRef.current;
    if (id) {
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: m.content + text } : m)));
      return;
    }
    const fresh = newId();
    streamIdRef.current = fresh;
    setStreaming(true);
    setMessages((prev) => [...prev, { id: fresh, role: 'assistant', content: text }]);
  };

  /** A turn that failed after its stream began: the half-written bubble goes. */
  const discardStream = () => {
    const id = streamIdRef.current;
    streamIdRef.current = null;
    if (id) setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  /**
   * The pass a send carries (ADR-123). `log` attaches one when it has one and
   * never waits; `enforce` waits for it. After a refusal the pass is always a
   * fresh one. Rejects with a HumanCheckError when no pass can be had.
   */
  const passForSend = async (afterRefusal: boolean): Promise<string | null> => {
    if (afterRefusal) return humanCheck.obtain({ fresh: true });
    const ready = humanCheck.current();
    if (ready) return ready;
    const mode = humanCheck.mode();
    if (mode === 'enforce') return humanCheck.obtain();
    if (mode === 'log') humanCheck.obtain().catch(() => undefined);
    return null;
  };

  /** A 403 asking for the check: adopt its configuration and drop the stale pass. */
  const refusedByHumanCheck = (status: number, body: Record<string, unknown> | null): boolean => {
    if (!isHumanCheckRefusal(status, body)) return false;
    humanCheck.learn(body?.humanCheck);
    humanCheck.invalidate();
    return true;
  };

  /**
   * One attempt on the wire. `refused` = the human check turned it away
   * before anything was admitted, metered or stored; anything else that goes
   * wrong throws.
   */
  const sendOnce = async (
    turn: OutgoingTurn,
    priorMessages: Message[],
    humanPass: string | null,
  ): Promise<'settled' | 'refused'> => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), MESSAGE_TIMEOUT_MS);
    const payload = {
      message: turn.message,
      tenantId,
      // The PAGE's language — evidence for the backend's one decider, and the
      // answer only when nothing the visitor did says anything (ADR-110 Am.11).
      language,
      // The visitor's choice in the header's control, '' for Automatic. ALWAYS
      // sent: the field's presence is how the backend knows this widget draws
      // its booking in the reply's language, so the booking may follow the
      // conversation rather than the page (1.32.0).
      chosenLanguage: languageChoice ? chosenLanguage : '',
      ...(sessionIdRef.current ? { sessionId: sessionIdRef.current } : {}),
      history: priorMessages
        .slice(0, -1)
        // The operator's messages and the widget's own notices are not turns.
        .filter((m) => !m.error && m.role !== 'admin' && m.role !== 'notice')
        .map((m) => ({ role: m.role, content: m.content })),
      // A booking details form's values (ADR-110); nothing else changes.
      ...(turn.details ? { details: turn.details } : {}),
      // ...and the find-booking form's (ADR-110 Amendment 10).
      ...(turn.lookup ? { lookup: turn.lookup } : {}),
      ...(humanPass ? { humanPass } : {}),
    };
    try {
      // The streaming transport first, when the server advertised one
      // (ADR-103 §4). The one fallback is a URL that could not be reached
      // at all — nothing after the first byte is resent, because a turn
      // that reached the backend was admitted and metered.
      if (streamUrl) {
        const outcome = await streamMessage(
          `${streamUrl}/chat/message`,
          payload,
          { onSession: adoptSession, onDelta: appendDelta },
          ctrl.signal,
        );
        if (outcome.kind === 'done') {
          applyReply(outcome.body);
          return 'settled';
        }
        if (outcome.kind === 'error') {
          discardStream();
          if (refusedByHumanCheck(outcome.status, outcome.body)) return 'refused';
          if (applyBlocked(outcome.status, outcome.body)) return 'settled';
          throw new TurnError('http', outcome.status);
        }
        if (outcome.kind === 'broken') {
          discardStream();
          // The connection dropped mid-answer.
          throw new TurnError('network');
        }
        console.warn(
          `[PweChat] The streaming endpoint ${streamUrl} could not be reached (${outcome.reason}); sending over ${apiUrl} instead.`,
        );
      }

      const res = await fetch(`${apiUrl}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ctrl.signal,
        body: JSON.stringify(payload),
      });

      if (res.status === 403) {
        const detail = await res.json().catch(() => ({}));
        if (refusedByHumanCheck(403, detail)) return 'refused';
        if (applyBlocked(403, detail)) return 'settled';
      }

      if (!res.ok) throw new TurnError('http', res.status);

      applyReply(await res.json());
      return 'settled';
    } finally {
      clearTimeout(timer);
    }
  };

  /** priorMessages must already end with the visitor's bubble for `turn`. */
  const doSend = async (turn: OutgoingTurn, priorMessages: Message[]) => {
    setLoading(true);
    try {
      // A human-check refusal spent nothing (ADR-123), so the turn goes again,
      // once, with a fresh pass. A second refusal is a failed check.
      let outcome = await sendOnce(turn, priorMessages, await passForSend(false));
      if (outcome === 'refused') outcome = await sendOnce(turn, priorMessages, await passForSend(true));
      if (outcome === 'refused') throw new HumanCheckError('refused_again');
    } catch (err) {
      discardStream();
      setLastFailed(turn);
      setLastFailure(err instanceof HumanCheckError ? null : classifyFailure(err));
      setReportState('idle');
      pushAssistant(err instanceof HumanCheckError ? ui.humanCheckFailed : errorMessage, true);
    } finally {
      setLoading(false);
      setStreaming(false);
      // Polling goes hot from HERE, once the reply and any new sessionId are
      // known: bumping before the request restarted the poll effect (and a
      // history GET) once per send and again on the adopted session.
      bumpActivity();
    }
  };

  /**
   * Every send: typed text, a suggestion chip, a booking step. `shown` is the
   * visitor's bubble; `turn` is what goes on the wire — the same text for
   * anything typed, and for a booking step the value the server reads under
   * the label the visitor tapped. Returns false when nothing was sent.
   */
  const sendTurn = (shown: string, turn: OutgoingTurn): boolean => {
    if (!shown || !turn.message || loading) return false;
    const updated = [...messages, { id: newId(), role: 'user' as const, content: shown }];
    setMessages(updated);
    setInput('');
    setSuggestions([]);
    // The last turn's booking steps go with the send, not with the answer.
    setActions([]);
    setLastFailed(null);
    // Keep the caret where it was: the input stays enabled — and EDITABLE —
    // while the reply is loading, so the browser never blurs it, and keyboard
    // users are not sent back to the box after every reply (review S7). A
    // chip tap on a phone does not pull the keyboard up, though.
    //
    // 🚨 It used to be `readOnly` while loading (1.29.0). On iOS a tap on a
    // read-only box focuses it WITHOUT a keyboard, and when the reply landed
    // and the box became editable again it was already focused, so the next
    // tap raised nothing (the effect on `open` says why). Typing while a
    // reply is on its way costs nothing: the send is refused above until it
    // has landed, and the words stay in the box.
    const refocus = !isMobile || document.activeElement === inputRef.current;
    doSend(turn, updated).then(() => {
      if (refocus) inputRef.current?.focus();
    });
    return true;
  };

  const sendText = (text: string) => {
    sendTurn(text, { message: text });
  };

  /**
   * "Report this issue": the product, the component, the kind of failure and
   * the page's path go to the status service (statusReport.ts). One report
   * per failed turn; a report that did not go through can be sent again.
   */
  const reportFailure = async () => {
    if (!statusReport || !lastFailure || reportState === 'sending' || reportState === 'done') return;
    setReportState('sending');
    const ok = await sendReport(statusReport, lastFailure, window.location.pathname);
    setReportState(ok ? 'done' : 'failed');
    if (!ok) console.warn(`[PweChat] The report could not be sent to ${statusReport.apiUrl}/v1/reports.`);
  };

  const retry = () => {
    if (!lastFailed || loading) return;
    const turn = lastFailed;
    setLastFailed(null);
    setActions([]);
    // Strip the failure bubble(s); the user message stays and the turn is
    // re-sent as-is — a slot's instant, a details form's values included.
    const base = messages.filter((m) => !m.error);
    setMessages(base);
    doSend(turn, base);
  };

  const sendMessage = (e: FormEvent) => {
    e.preventDefault();
    // A paste past the limit is kept whole and named (below the box); it is
    // never sent cut — POST /chat/message refuses a message over the limit.
    if (input.length > MESSAGE_MAX) return;
    sendText(input.trim());
  };

  const startNewChat = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = setTimeout(() => setConfirmClear(false), 3000);
      return;
    }
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setConfirmClear(false);
    setMessages([{ id: 'greeting', role: 'assistant', content: greeting }]);
    setSuggestions(config.suggestions);
    setSessionId(null);
    setLastFailed(null);
    setReveal(null);
    setPollStopped(false);
    setLeadOpen(false);
    setLeadSubmitted(false);
    setBookingTaken(false);
    setLinkChipHidden(false);
    setActions([]);
    // A new conversation has no language of its own yet; a CHOICE outlives
    // it — the visitor asked for that language, not for that conversation.
    setConversationLanguage('');
    seenAdminIdsRef.current = new Set();
    lastSkRef.current = null;
    clearChat(tenantId);
    bumpActivity();
  };

  // ── Operator replies ─────────────────────────────────────────
  useHistoryPolling({
    enabled: open && !pollStopped,
    apiUrl,
    tenantId,
    sessionId,
    activityAt,
    cursorRef: lastSkRef,
    seenAdminIdsRef,
    onAdminMessages: (contents) => {
      setMessages((prev) => [...prev, ...contents.map((content) => ({ id: newId(), role: 'admin' as const, content }))]);
      setAnnouncement(stripMarkdown(contents[contents.length - 1]));
    },
    onGone: () => setPollStopped(true),
    onClosed: () => setPollStopped(true),
  });

  // ── Lead capture ─────────────────────────────────────────────
  // The country the phone box starts on (1.31.0): the business's choice or
  // its country when the server says, else the page's own region, else GB.
  const phoneCountry = defaultPhoneCountry(leadForm.phoneCountry, language);
  const handleLead = async (lead: LeadInput, firstName: string, drawn: readonly string[]): Promise<string | Refusal | null> => {
    const sid = sessionIdRef.current;
    const request = {
      tenantId,
      ...(sid ? { sessionId: sid } : {}),
      ...lead,
      conversation_summary: summariseConversation(messagesRef.current),
      // The language the acknowledgement e-mail is written in is decided from
      // these by chat_lead, with the conversation's own rule (ADR-114 Am.5).
      language: uiLanguage,
      chosenLanguage: languageChoice ? chosenLanguage : '',
    };
    // The same pass a turn carries (ADR-123), and the same one retry.
    let outcome: LeadOutcome;
    try {
      const humanPass = await passForSend(false);
      outcome = await submitLead(apiUrl, { ...request, ...(humanPass ? { humanPass } : {}) });
      if (outcome.kind === 'human_check') {
        humanCheck.learn(outcome.humanCheck);
        humanCheck.invalidate();
        const fresh = await passForSend(true);
        outcome = await submitLead(apiUrl, { ...request, ...(fresh ? { humanPass: fresh } : {}) });
      }
    } catch {
      return ui.humanCheckFailed;
    }
    switch (outcome.kind) {
      case 'ok':
        setLeadOpen(false);
        setLeadSubmitted(true);
        pushAssistant(fill(ui.leadThanks, { name: firstName }));
        return null;
      case 'disabled':
        // Switched off in the console after this page loaded: no chip from
        // now on, and the visitor is told their details did not go through.
        setLeadOpen(false);
        setLeadDisabled(true);
        pushAssistant(ui.leadError, true);
        return null;
      case 'blocked':
        setLeadOpen(false);
        setLeadDisabled(true);
        pushAssistant(outcome.code === 'origin_not_allowed' ? originBlockedMessage : ui.unavailable, true);
        return null;
      case 'invalid': {
        // The backend refused a field the client check let through: its
        // `{code, field}` (1.31.0), drawn against the box it names in the
        // visitor's language. A box this page never drew is the form as a
        // whole — the business changed it since the page loaded.
        const labels: Record<string, string> = {
          firstName: ui.detailsFirstName,
          lastName: ui.detailsLastName,
          email: ui.detailsEmail,
          phone: ui.detailsPhone,
          message: ui.leadMessageLabel,
        };
        const said = refusalSentence(
          {
            ui,
            language: uiLanguage,
            label: (field) => labels[field] ?? leadForm.extraFields.find((f) => f.id === field)?.label ?? '',
            extraFields: leadForm.extraFields,
            // A phone refusal that names no country is about the one chosen.
            phoneCountry: lead.phoneCountry,
          },
          outcome.refusal,
          drawn,
        );
        return said.field ? said : said.text;
      }
      case 'human_check':
        return ui.humanCheckFailed;
      case 'retry':
        return outcome.code === 'lead_cap' ? ui.leadCap : ui.leadError;
    }
  };

  const openLeadForm = () => {
    setLeadOpen(true);
    setLastFailed(null);
    bumpActivity();
  };

  /**
   * "Book an appointment": an ordinary turn carrying the chip's OWN words.
   *
   * Not a canonical English phrase behind a translated label — the backend's
   * booking hints read all five languages since ADR-110 Amendment 7, so the
   * label a visitor tapped is a booking request in their language, and the
   * bubble they get back says exactly what they pressed. `tests/
   * test_booking_languages.py` pins that pairing from the backend's side:
   * change a label here and the hint that admits it is checked.
   */
  const startBooking = () => {
    sendText(ui.bookAppointment);
  };

  // ── Booking steps (ADR-110) ──────────────────────────────────
  /** A details submit: a line that never carries the email, and the values as `details`. */
  const submitDetails = (details: BookingDetails, firstName: string): boolean => {
    const line = bookingDetailsLine(firstName, ui);
    return sendTurn(line, { message: line, details });
  };

  /** Cancel closes the form and leaves the conversation as it is; typing still works. */
  const cancelDetails = () => {
    setActions((prev) => prev.filter((a) => a.type !== 'collect_details'));
    if (!isMobile) inputRef.current?.focus();
  };

  /** A find-booking submit (ADR-110 Am.10): the reference in the line, the detail only in `lookup`. */
  const submitLookup = (lookup: BookingLookup): boolean => {
    const line = lookupLine(lookup.booking_reference, ui);
    return sendTurn(line, { message: line, lookup });
  };

  const cancelLookup = () => {
    setActions((prev) => prev.filter((a) => a.type !== 'find_booking'));
    if (!isMobile) inputRef.current?.focus();
  };

  // ── The info panel (1.19.0) ──────────────────────────────────
  const closeInfo = () => {
    setInfoOpen(false);
    infoButtonRef.current?.focus();
  };

  const toggleInfo = () => {
    // The two-tap "new chat" is not on screen while the panel is.
    setConfirmClear(false);
    // One popover at a time: the language menu hangs from the same header.
    setLanguageOpen(false);
    setInfoOpen((prev) => !prev);
  };

  /**
   * A press anywhere else closes it. On the DOCUMENT in the capture phase and
   * on `pointerdown` rather than `click`, for the same reason the host-button
   * listener is (`launcherSelector`): a host page that stops propagation on
   * its own overlays would otherwise leave the panel open over the
   * conversation with no way back but the button itself.
   */
  useEffect(() => {
    if (!infoOpen) return;
    const away = (e: Event) => {
      const target = e.target as Node | null;
      if (target && (infoPanelRef.current?.contains(target) || infoButtonRef.current?.contains(target))) return;
      setInfoOpen(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [infoOpen]);

  // ── The language control (1.32.0, ADR-128 Amendment 4) ───────
  // Automatic by default, and the visitor may choose one of the five. A
  // choice sticks — typing in another language does not undo it (the owner's
  // decision, 2026-09-25) — for this conversation and the next on this site;
  // "Automatic" takes it back. One line in the conversation says what changed,
  // in the new language, and is never sent.
  const languageButtonRef = useRef<HTMLButtonElement>(null);
  const languagePanelRef = useRef<HTMLDivElement>(null);

  const closeLanguage = () => {
    setLanguageOpen(false);
    languageButtonRef.current?.focus();
  };

  const toggleLanguage = () => {
    setConfirmClear(false);
    setInfoOpen(false);
    setLanguageOpen((prev) => !prev);
  };

  const chooseLanguage = (choice: Choosable | '') => {
    closeLanguage();
    if (choice === chosenLanguage) return;
    setChosenLanguage(choice);
    if (persist) writeChoice(tenantId, choice);
    // The line is in the language the widget speaks FROM NOW ON: the choice,
    // or — back to Automatic — the conversation's, else the page's.
    const next = drawnLanguage(choice, conversationLanguage, language);
    const text = choiceNotice(choice, stringsFor(next), businessName);
    setMessages((prev) => [...prev, { id: newId(), role: 'notice', content: text, language: next }]);
    setAnnouncement(text);
  };

  useEffect(() => {
    if (!languageOpen) return;
    const away = (e: Event) => {
      const target = e.target as Node | null;
      if (target && (languagePanelRef.current?.contains(target) || languageButtonRef.current?.contains(target))) return;
      setLanguageOpen(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [languageOpen]);

  // ── Feedback about the assistant (feedback.ts) ───────────────
  const openFeedback = () => {
    // The two-tap "new chat" is not on screen while the form is.
    setConfirmClear(false);
    setInfoOpen(false);
    setLanguageOpen(false);
    setFeedbackOpen(true);
  };

  const closeFeedback = () => {
    setFeedbackOpen(false);
    bumpActivity();
  };

  /** Resolves to the error to show on the form, or null once it has gone. */
  const handleFeedback = async (input: FeedbackInput): Promise<string | null> => {
    if (!feedbackApiUrl) return ui.feedbackError;
    // A filled honeypot is sent like any other submission: the feedback
    // service answers it 201 and keeps nothing (product-ema-feedback#62), as
    // chat_lead does for the lead form. One check, on the server (1.32.1).
    const outcome = await submitFeedback(
      feedbackApiUrl,
      feedbackRequest(input, { tenantId, language: uiLanguage, page: feedbackPage(window.location) }),
    );
    if (outcome.kind === 'ok') {
      setAnnouncement(ui.feedbackThanks);
      return null;
    }
    console.warn(`[PweChat] Feedback could not be sent to ${feedbackApiUrl}/v1/feedback (${outcome.code}).`);
    return ui.feedbackError;
  };

  // ── Placement (corner + offset, with mobile clamp) ───────────
  //
  // 🚨 THE WIDGET'S VIEWPORT IS ITS CONTAINING BLOCK, NOT THE WINDOW (1.20.0).
  //
  // The launcher and the panel are `position: fixed`, so the box they resolve
  // against is the nearest ancestor with a transform — the window on a client's
  // page, where there is none, and something much smaller wherever one exists.
  // The console's Live preview is exactly that: `.ema-assistant-pane` carries
  // `translateZ(0)` on purpose, so the pane's corner is the widget's corner.
  //
  // Measuring `window` there was wrong in the way a client can see. On the
  // console's Home page (2026-09-15) the pane is ~410px tall; the widget read
  // a 900px window, drew its 520px panel, and the composer — the one control a
  // preview exists for — was off the bottom of the pane. The console had
  // clamped the panel back with `!important` rules of its own, which is the
  // shape of a fix that cannot know what it is hiding.
  //
  // `probeRef` is a `position: fixed; inset: 0` box. Whatever size it reports
  // IS the box the launcher and the panel hang in, on a page or in a pane,
  // with no guess about which. On a client's site it is the layout viewport,
  // so nothing about a real install changes.
  //
  // The VISUAL viewport still caps the height: iOS Safari does not shrink the
  // layout viewport for the keyboard, so a sheet measured from it put the
  // input under the keys (review S9). The smaller of the two is right in both
  // cases — the keyboard shrinks the visual one, and a pane makes the probe
  // the smaller by far.
  //
  // And the WIDTH, since 1.28.0, for the same reason: a zoomed page (Safari
  // zooms in on a small field taking focus; a visitor pinches) shows only
  // part of the layout viewport, and a sheet as wide as the probe hung off
  // the side of an iPhone's screen with the Send button out of sight. `left`
  // is the visual viewport's offset on that axis, as `top` is on the other.
  const probeRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<{ w: number; h: number; top: number; left: number }>({
    w: 1024,
    h: 768,
    top: 0,
    left: 0,
  });
  useEffect(() => {
    const vv = window.visualViewport;
    const update = () => {
      const box = probeRef.current?.getBoundingClientRect();
      const w = Math.round(box?.width || (vv ? vv.width : window.innerWidth));
      const h = Math.round(box?.height || (vv ? vv.height : window.innerHeight));
      const visualW = Math.round(vv ? vv.width : window.innerWidth);
      const visualH = Math.round(vv ? vv.height : window.innerHeight);
      setViewport({
        w: Math.min(w, visualW),
        h: Math.min(h, visualH),
        top: Math.round(vv ? vv.offsetTop : 0),
        left: Math.round(vv ? vv.offsetLeft : 0),
      });
    };
    update();
    window.addEventListener('resize', update);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    // A pane can be resized without the window moving at all — the console's
    // own full-screen and phone-width controls do exactly that.
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    if (observer && probeRef.current) observer.observe(probeRef.current);
    return () => {
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, []);

  /**
   * The probe itself: inert, invisible, out of the accessibility tree and out
   * of the way of every pointer. It is rendered in every state of the widget,
   * open or closed, because the launcher hangs in the same box the panel does.
   */
  const viewportProbe = (
    <div
      ref={probeRef}
      aria-hidden="true"
      style={{ position: 'fixed', inset: 0, pointerEvents: 'none', visibility: 'hidden', zIndex: -1 }}
    />
  );

  const isLeft = position === 'bottom-left';
  const isExpanded = panelSize === 'expanded';
  /** The first letter of the business's name, for the header mark when there is no avatar. */
  const initial = (businessName ?? '').trim().charAt(0).toUpperCase();
  // Height counts too: a landscape phone is 380px tall and the floating card
  // has little room; it gets the full-screen sheet like a portrait one.
  //
  // 🚨 BUT A SHORT BOX IS NOT A SHORT DEVICE (1.20.0). Now that the viewport
  // is the containing block, the console's preview pane is a short box on a
  // laptop — and a full-bleed sheet across a 880px-wide pane is not what that
  // pane exists to show, which is a website with a widget in the corner of it.
  // So the height clause asks whether this is a touch device at all. A width
  // of 480 or less is a phone either way and needs no such question.
  const isMobile = viewport.w <= 480 || (viewport.h <= 480 && coarsePointer);

  // On mobile the panel is clamped to the viewport; clamp offsets to the same
  // box so nothing anchors off-screen.
  const panelW = Math.min(isExpanded ? 560 : 380, viewport.w - 48);
  const panelH = Math.min(isExpanded ? viewport.h - 48 : 520, viewport.h - 48);
  /**
   * 🚨 A SHORT PANEL DROPS WHAT IT CAN SPARE (1.20.0).
   *
   * Below this the conversation and the composer are what is left: the header,
   * the language line and the suggestion chips go, and the two standing calls
   * to action stay, because they are what the client is paying for and a
   * visitor who cannot see them has to ask for them in words ([ADR-128]).
   *
   * 420px is measured, not chosen: at the console's preview height the panel
   * is ~360px, and the header (72), greeting (56), language line (20),
   * three suggestions (112), the standing row (44) and the composer (66) come
   * to more than that before a single reply arrives.
   */
  const compact = panelH < 420;
  /**
   * Whether the visitor can still close the panel without its header. The
   * floating launcher shows an × while the panel is open — but not on a phone,
   * where the sheet covers it, and not when the host page owns the button
   * (`externalTrigger` / `launcherSelector`), where there is no launcher at
   * all. Wherever it is the only way out, the header STAYS, stripped to its
   * buttons rather than removed: a panel a visitor cannot close is worse than
   * a panel with 34px less conversation in it.
   */
  const closableWithoutHeader = !isMobile && !hostLaunched;
  const hideHeader = compact && closableWithoutHeader;
  const slimHeader = compact && !hideHeader;
  const maxSide = Math.max(0, viewport.w - 24 - panelW);
  const maxBottom = Math.max(0, viewport.h - 24 - panelH);
  const sideOffset = isMobile ? Math.min(offset.side, maxSide) : offset.side;
  const bottomOffset = isMobile ? Math.min(offset.bottom, maxBottom) : offset.bottom;

  // The full-screen sheet covers the page; stop the page scrolling under it.
  useEffect(() => {
    if (!open || !isMobile) return;
    const body = document.body;
    const previous = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previous;
    };
  }, [open, isMobile]);

  /**
   * A link into the page the visitor is on scrolls THAT page (ADR-141
   * Amendment 1, sectionLink.ts) instead of opening a copy of it in a new tab.
   * Only a plain click: a modified or middle click asked for a tab and gets
   * one. On a phone the sheet covers the page, so it closes first — the
   * conversation is kept, and the launcher brings it back. A section the
   * page does not have (any more) leaves the click alone: the link opens as
   * it always has.
   */
  const followSectionLink = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || typeof window === 'undefined') return;
      const target = sectionOnThisPage(anchor.href, window.location.href);
      const section = target ? findSection(target, document) : null;
      if (!section) return;
      event.preventDefault();
      if (!isMobile) {
        showSection(section, window);
        return;
      }
      setOpen(false);
      // After the sheet has gone and the page scrolls again.
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => showSection(section, window)));
    },
    [isMobile],
  );

  const styles = useMemo(
    () =>
      buildStyles({
        brandColor,
        isLeft,
        isMobile,
        isExpanded,
        coarsePointer,
        viewport,
        sideOffset,
        bottomOffset,
      }),
    [brandColor, isLeft, isMobile, isExpanded, coarsePointer, viewport, sideOffset, bottomOffset],
  );

  // What the latest turn's booking steps draw, labelled in the conversation's
  // language (1.32.0) — the one the core just spoke, so a day chip never reads
  // "mer 30 set" beside an English question.
  const booking = useMemo(() => {
    const booked = actions.find((a): a is Extract<ChatAction, { type: 'booked' }> => a.type === 'booked');
    // A booking the visitor already has (ADR-110 Amendment 10): the card a
    // lookup found, or the one a cancellation leaves. At most one of the two.
    const held = actions.find(
      (a): a is Extract<ChatAction, { type: 'found_booking' | 'cancelled' }> => a.type === 'found_booking' || a.type === 'cancelled',
    );
    return {
      choices: bookingChoices(actions, uiLanguage, ui),
      details: actions.find((a): a is Extract<ChatAction, { type: 'collect_details' }> => a.type === 'collect_details'),
      find: actions.find((a): a is Extract<ChatAction, { type: 'find_booking' }> => a.type === 'find_booking'),
      booked,
      bookedAt: booked ? formatSlotLabel(booked.start, uiLanguage, booked.timezone) : '',
      bookedWas: booked?.changedFrom ? formatSlotLabel(booked.changedFrom, uiLanguage, booked.timezone) : '',
      held,
      heldAt: held ? formatSlotLabel(held.start, uiLanguage, held.timezone) : '',
      heldCancelled: held ? held.type === 'cancelled' || held.status === 'cancelled' : false,
      hasControls: actions.some(isBookingControl),
    };
  }, [actions, uiLanguage, ui]);

  // ── The placeholder says it takes any language (1.18.0) ──────
  // The page's own words first, then the other four in turn, so a visitor who
  // glances at an empty input learns they may write in theirs — the one thing
  // about this assistant that no amount of English copy can convey.
  //
  // Only while the box is empty (a placeholder is invisible once there is
  // text), and never under `prefers-reduced-motion`: a caption that rewrites
  // itself beside a cursor is exactly what that setting is asked for. The
  // input's aria-label does NOT rotate — an accessible name that changes
  // under a screen reader is a different control every few seconds.
  const placeholders = useMemo(() => [ui.typeAMessage, ...ui.typeAMessageIn], [ui]);
  const [placeholderAt, setPlaceholderAt] = useState(0);

  useEffect(() => {
    setPlaceholderAt(0);
    if (!open || input !== '' || loading || placeholders.length < 2) return;
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setInterval(
      () => setPlaceholderAt((i) => (i + 1) % placeholders.length),
      PLACEHOLDER_ROTATE_MS,
    );
    return () => clearInterval(timer);
  }, [open, input, loading, placeholders]);

  // ── Keyframes (injected once per page) ───────────────────────
  useEffect(() => {
    if (document.getElementById(KEYFRAMES_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = KEYFRAMES_STYLE_ID;
    style.textContent = KEYFRAMES_CSS;
    document.head.appendChild(style);
  }, []);

  // ── Render ───────────────────────────────────────────────────
  const visibleContent = (msg: Message): string =>
    reveal && reveal.id === msg.id ? msg.content.slice(0, reveal.chars) : msg.content;

  const dialogLabel = fill(ui.chatDialogLabel, { name: businessName });
  const teamLabel = fill(ui.teamLabel, { name: businessName });

  const launcherIcon = avatarUrl ? (
    // The button carries the accessible name; the picture is decorative.
    <img src={avatarUrl} alt="" width="36" height="36" style={{ borderRadius: '50%', objectFit: 'cover' }} />
  ) : (
    <ChatIcon />
  );

  const chipHover = {
    onMouseEnter: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.background = styles.chipHoverBackground;
      e.currentTarget.style.borderColor = styles.accent;
    },
    onMouseLeave: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.background = 'rgba(255,255,255,0.8)';
      e.currentTarget.style.border = styles.chipBorder;
    },
  };

  // The standing chips are already filled, so hovering deepens the fill rather
  // than replacing it — the outlined chips' hover would read as a press.
  const primaryChipHover = {
    onMouseEnter: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.background = styles.primaryChipHoverBackground;
    },
    onMouseLeave: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.background = String(styles.primaryChip.background);
    },
  };

  const chips: Chip[] = suggestions.map((text) => ({ kind: 'text', text }));
  // The business's own call to action leads the row: the one chip a client
  // put there on purpose, and the one that is always there.
  if (linkChip && !linkChipHidden) chips.unshift({ kind: 'link', label: linkChip.label, url: linkChip.url });
  // A booking control (slot or option chips, the details form, confirm) stands
  // in for the suggestions while it is shown; the text input stays usable.
  const showBookingControls = bookingSettled && !leadOpen && booking.hasControls;
  // 🚨 `!compact`: in a short panel the suggestions are what gives way. They
  // are questions a visitor MIGHT ask; the row below them is the two things
  // the business is paying for, and the composer is how anything else gets
  // asked. Three suggestion chips are 112px of a 360px panel.
  const showChips = !compact && chips.length > 0 && !loading && !reveal && !lastFailed && !leadOpen && !showBookingControls && !feedbackOpen;

  // ── The two standing calls to action (1.18.0) ────────────────
  // Leaving your details and taking an appointment are what this assistant is
  // FOR, and until now they were a chip at the end of a suggestion row that
  // every reply replaced and one × swept away. They now stand in a row of
  // their own, below the conversation and above the input, from the first
  // paint of the panel.
  //
  // Each retires on its OWN completion and nothing else: details sent takes
  // the first away, an appointment taken takes the second, and a form the
  // visitor opens and cancels leaves both exactly where they were. A capability
  // the client has not switched on was never drawn in the first place.
  const primaryActions: PrimaryAction[] = [];
  if (leadCaptureEnabled && !leadDisabled && !leadSubmitted) primaryActions.push('lead');
  if (bookingEnabled && !bookingTaken) primaryActions.push('booking');
  // Mid-booking, the steps ARE the call to action: a "Book an appointment"
  // chip beside the times on offer would ask the visitor to start again.
  const showPrimaryActions =
    primaryActions.length > 0 && !leadOpen && !feedbackOpen && !showBookingControls;

  // The language line sits with the greeting and goes as soon as there is a
  // conversation to read: it is an invitation, and an invitation repeated
  // under every reply is clutter. The placeholder carries it from there.
  // The same rule, for the same reason: an invitation is worth 20px when
  // there is room for it, and the rotating placeholder says it anyway.
  const showLanguageNote = !compact && messages.length === 1 && !leadOpen && !feedbackOpen && !loading;

  // The header's language control (1.32.0): offered where the backend decides
  // the conversation's language and the business has not pinned one
  // (`languageChoice`, config.ts). The compact panel drops it with the rest
  // of the header's extras; Automatic still works there.
  const showLanguageControl = languageChoice && !compact && !feedbackOpen;
  // A language in its own name where it is one of the five, else its code.
  const languageName = (tag: string) => {
    const known = choosable(tag);
    return known ? ENDONYMS[known] : languageCode(tag);
  };
  const automaticNow = languageName(drawnLanguage('', conversationLanguage, language));

  // The human check's frame is ALWAYS the first child, open or closed: a frame
  // that moves to another parent is remounted and starts the challenge again.
  if (!open) {
    if (hostLaunched) return <>{viewportProbe}{humanCheckFrame}</>;
    return (
      <>
        {viewportProbe}
        {humanCheckFrame}
        <div style={styles.bubbleGlow} />
        {/* The invitation, beside the bubble, in the visitor's words. Desktop
            only: a phone has no room beside the bubble and the bubble is the
            whole affordance there. Hidden from assistive tech and the tab
            order on purpose — the bubble already carries the accessible name
            ("Open chat"), and a second focus stop that does the same thing is
            noise to a screen reader; a pointer gets the larger target. */}
        {launcherLabel && !isMobile && (
          <div
            style={styles.launcherLabel}
            onClick={() => setOpen(true)}
            aria-hidden="true"
            data-pwe="launcher-label"
          >
            {launcherLabel}
          </div>
        )}
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          style={styles.bubble}
          aria-label={ui.openChat}
          title={ui.chatWithUs}
          data-pwe="launcher"
        >
          {launcherIcon}
        </button>
      </>
    );
  }

  return (
    <>
      {viewportProbe}
      {humanCheckFrame}
      {/* Floating button (shows X when open; hidden under the mobile sheet).

          🚨 DRAWN AT EVERY PANEL SIZE (1.22.0). It was hidden while the panel
          was expanded in 1.21.0, to keep it off the composer's Send button —
          and that took away the only control that opens the panel again once
          it is shut. The overlap is fixed where it was caused instead: the
          expanded panel hangs from the same 96px anchor as the card, so the
          bubble keeps its room (`theme.ts`). */}
      {!hostLaunched && !isMobile && (
        <>
          <div style={styles.bubbleGlow} />
          <button
            ref={launcherRef}
            type="button"
            onClick={() => closePanel(true)}
            style={styles.bubble}
            aria-label={ui.closeChat}
            data-pwe="launcher"
          >
            <CloseIcon size={20} />
          </button>
        </>
      )}

      {/* Chat panel */}
      {/* `data-pwe-size` is for a HOST that has to know: the console's preview
          pane draws a stand-in site header the panel hangs below, and how far
          below depends on which size it is (the anchor is 96px or 24px). */}
      <div
        style={styles.panel}
        role="dialog"
        aria-label={dialogLabel}
        onKeyDown={onPanelKeyDown}
        data-pwe="panel"
        data-pwe-size={panelSize}
        // The language the widget speaks (1.32.0): what a screen reader reads
        // its own words in. Each bubble says its own below.
        lang={uiLanguage}
      >
        {/* Screen-reader announcements: complete replies only, so the
            typewriter reveal doesn't spam partial text. */}
        <div style={srOnlyStyle} aria-live="polite">
          {announcement}
        </div>

        {/* Header, and the info panel that hangs off the bottom of it. In a
            short panel the header goes entirely where the launcher can still
            close the widget, and is stripped to its buttons where it cannot
            (`hideHeader` / `slimHeader` above). The info panel still hangs off
            this wrapper either way. */}
        <div style={{ position: 'relative', flexShrink: 0 }} data-pwe-compact={compact ? 'true' : undefined}>
        {!hideHeader && (
        <div style={slimHeader ? { ...styles.header, padding: '6px 10px', justifyContent: 'flex-end' } : styles.header} data-pwe="header">
          {!slimHeader && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            {/* The business's mark, always (1.29.0): its avatar where it set
                one and the image loads, and otherwise a brand-coloured disc
                with the business's initial. A header that began with the
                name alone read as unbranded, and an avatar a client site's
                CSP blocks left the same blank without a word. */}
            {avatarUrl && !avatarBroken ? (
              <img
                src={avatarUrl}
                alt=""
                width="32"
                height="32"
                style={{ flexShrink: 0, borderRadius: '50%', objectFit: 'cover' }}
                onError={() => setAvatarBroken(true)}
                data-pwe="avatar"
              />
            ) : (
              <span aria-hidden="true" style={styles.headerMark} data-pwe="mark">
                {initial || <ChatIcon size={16} strokeWidth={2} />}
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '15px', color: '#1a1a2e', letterSpacing: '-0.01em' }} data-pwe="title">
                {businessName}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>{feedbackOpen ? ui.feedbackSubtitle : subtitle}</div>
            </div>
          </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {confirmClear && (
              // Inline, not only in a tooltip: the second tap is otherwise a
              // guess for anyone not hovering a mouse (review N7).
              <span role="status" style={{ fontSize: '11px', color: '#b91c1c', whiteSpace: 'nowrap' }}>
                {ui.tapAgainToClearShort}
              </span>
            )}
            {showLanguageControl && (
              // The language in force, and the way to change it (1.32.0,
              // ADR-128 Am.4): a globe and the code — never a flag.
              <button
                ref={languageButtonRef}
                type="button"
                onClick={toggleLanguage}
                style={{
                  ...styles.iconButton,
                  borderRadius: '999px',
                  padding: '5px 9px',
                  gap: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  ...(languageOpen || chosenLanguage ? { background: styles.chipHoverBackground, color: styles.accent } : {}),
                }}
                aria-label={fill(ui.languageButton, { language: languageName(uiLanguage) })}
                title={fill(ui.languageButton, { language: languageName(uiLanguage) })}
                aria-expanded={languageOpen}
                data-pwe="language"
                data-language={choosable(uiLanguage) || uiLanguage}
                data-chosen={chosenLanguage || undefined}
              >
                <GlobeIcon size={13} />
                <span aria-hidden="true">{languageCode(uiLanguage)}</span>
              </button>
            )}
            {feedbackOpen ? (
              // While the form stands in for the conversation, this is the way
              // back to it — the same button that used to open the form.
              <button
                ref={feedbackButtonRef}
                type="button"
                onClick={closeFeedback}
                style={{ ...styles.iconButton, background: styles.chipHoverBackground, color: styles.accent }}
                aria-label={ui.backToChat}
                title={ui.backToChat}
                data-pwe="feedback"
              >
                <ChatIcon size={14} strokeWidth={2} />
              </button>
            ) : (
              <button
                ref={infoButtonRef}
                type="button"
                onClick={toggleInfo}
                style={{
                  ...styles.iconButton,
                  ...(infoOpen ? { background: styles.chipHoverBackground, color: styles.accent } : {}),
                }}
                aria-label={ui.aboutAssistant}
                title={ui.aboutAssistant}
                aria-expanded={infoOpen}
                data-pwe="info"
              >
                <InfoIcon />
              </button>
            )}
            {!feedbackOpen && messages.length > 1 && (
              <button
                type="button"
                onClick={startNewChat}
                style={{
                  ...styles.iconButton,
                  ...(confirmClear ? { background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c' } : {}),
                }}
                aria-label={confirmClear ? ui.confirmNewChat : ui.newChat}
                title={confirmClear ? ui.tapAgainToClear : ui.newChat}
                data-pwe="new-chat"
              >
                {confirmClear ? <CheckIcon /> : <PlusIcon />}
              </button>
            )}
            {!isMobile && (
              <button
                type="button"
                onClick={() => setPanelSize(isExpanded ? 'default' : 'expanded')}
                style={styles.iconButton}
                aria-label={isExpanded ? ui.collapseChat : ui.expandChat}
                title={isExpanded ? ui.collapseChat : ui.expandChat}
                data-pwe="expand"
              >
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </button>
            )}
            <button type="button" onClick={() => closePanel(true)} style={styles.iconButton} aria-label={ui.closeChat} data-pwe="close">
              <CloseIcon />
            </button>
          </div>
        </div>
        )}

        {/* What this assistant is, and how to say something about it (1.19.0).
            The privacy line used to sit under the composer on every screen;
            it says the same thing here, one tap away, and the conversation
            gets the line back. */}
        {infoOpen && (
          <div ref={infoPanelRef} style={styles.infoPanel} role="group" aria-label={ui.aboutAssistant} data-pwe="info-panel">
            <div style={{ fontWeight: 600, color: '#1a1a2e' }} data-pwe="info-version">
              {fill(ui.assistantVersion, { version })}
            </div>
            {/* ALWAYS rendered. This used to be `{privacyUrl && ...}`, so a
                tenant who never filled the field in shipped an assistant that
                stores what visitors type and says nothing — and the visitor,
                who chose none of this, was the one who paid for it. Without a
                tenant URL the line points at the operator's notice under a
                label that does not claim to be the business's policy. */}
            <div data-pwe="privacy">
              {privacyNotice}{' '}
              <a
                href={privacyUrl || OPERATOR_PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: styles.accent, textDecoration: 'underline' }}
              >
                {privacyUrl ? privacyLinkLabel : ui.privacyOperatorLinkLabel}
              </a>
              .
            </div>
            {feedbackApiUrl && (
              <button
                type="button"
                onClick={openFeedback}
                style={{ ...styles.chip, display: 'inline-flex', alignItems: 'center', gap: '6px', alignSelf: 'flex-start' }}
                {...chipHover}
                data-pwe="info-feedback"
              >
                <FeedbackIcon />
                {ui.sendFeedback}
              </button>
            )}
          </div>
        )}
        {/* The language menu hangs off the header like the info panel, from
            the same relative wrapper — outside it, `top: 100%` is the bottom
            of the whole panel, and the menu opens below the screen. */}
        {languageOpen && showLanguageControl && (
          <LanguageMenu
            ui={ui}
            styles={styles}
            chosen={chosenLanguage}
            automaticNow={automaticNow}
            onChoose={chooseLanguage}
            panelRef={languagePanelRef}
          />
        )}
        </div>

        {feedbackOpen && (
          <FeedbackForm ui={ui} styles={styles} brandColor={brandColor} onSubmit={handleFeedback} onClose={closeFeedback} />
        )}

        {/* Messages — hidden, not unmounted, behind the feedback form: a
            half-filled lead or booking form in the transcript survives it. */}
        <div
          style={feedbackOpen ? { display: 'none' } : styles.messages}
          data-pwe="messages"
          onClick={followSectionLink}
        >
          {messages.map((msg) =>
            msg.role === 'notice' ? (
              // The widget's own line (1.32.0): what changed about the
              // conversation, not a message in it — centred, quiet, and in
              // the language it announces.
              // Not a live region of its own: `announcement` already says it
              // once, and a second would read it twice.
              <div
                key={msg.id}
                lang={msg.language}
                style={{ alignSelf: 'center', fontSize: '12px', color: '#6b7280', textAlign: 'center', padding: '2px 12px' }}
                data-pwe="notice"
              >
                {msg.content}
              </div>
            ) : (
            <div
              key={msg.id}
              style={msg.role === 'user' ? styles.userBubble : msg.role === 'admin' ? styles.adminBubble : styles.assistantBubble}
              lang={msg.language}
              data-pwe="message"
              data-role={msg.role}
              data-error={msg.error ? 'true' : undefined}
            >
              {msg.role === 'admin' && (
                <div style={{ fontSize: '11px', fontWeight: 600, color: styles.accent, marginBottom: '2px' }}>{teamLabel}</div>
              )}
              {msg.role === 'user' ? (
                msg.content
              ) : (
                <AssistantMarkdown text={visibleContent(msg)} accent={styles.accent} />
              )}
            </div>
            ),
          )}
          {loading && !streaming && (
            <div style={styles.assistantBubble} role="status" aria-label={ui.typing} data-pwe="typing">
              <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }} aria-hidden="true">
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#8b8fa3',
                      animation: `pweb-chat-pulse 1.2s ease-in-out ${dot * 0.2}s infinite`,
                    }}
                  />
                ))}
              </span>
            </div>
          )}
          {/* A booking that went through: it informs and adds no button. Plain text, never markdown. */}
          {bookingSettled && booking.booked && (
            <div style={styles.assistantBubble} data-pwe="booking-booked" data-changed={booking.bookedWas ? 'true' : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: styles.accent }}>
                <CheckIcon />
                {booking.bookedWas ? ui.bookingChanged : ui.bookingBooked}
              </div>
              <div style={{ fontWeight: 600, marginTop: '2px' }}>{booking.bookedAt}</div>
              {booking.booked.service && <div>{booking.booked.service}</div>}
              {booking.bookedWas && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {fill(ui.bookingWas, { when: booking.bookedWas })}
                </div>
              )}
              {booking.booked.bookingId && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {fill(ui.bookingReference, { id: booking.booked.bookingId })}
                </div>
              )}
            </div>
          )}
          {/* A booking the visitor already has (ADR-110 Am.10): found, or just cancelled. Plain text. */}
          {bookingSettled && booking.held && (
            <div
              style={styles.assistantBubble}
              data-pwe={booking.held.type === 'cancelled' ? 'booking-cancelled' : 'booking-found'}
              data-status={booking.heldCancelled ? 'cancelled' : 'confirmed'}
            >
              <div style={{ fontWeight: 600, color: booking.heldCancelled ? '#6b7280' : styles.accent }}>
                {booking.heldCancelled ? ui.bookingCancelled : ui.bookingFound}
              </div>
              <div
                style={{ fontWeight: 600, marginTop: '2px', textDecoration: booking.heldCancelled ? 'line-through' : undefined }}
              >
                {booking.heldAt}
              </div>
              {booking.held.service && <div>{booking.held.service}</div>}
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {fill(ui.bookingReference, { id: booking.held.bookingId })}
              </div>
            </div>
          )}
          {showBookingControls && booking.find && (
            <FindBookingForm ui={ui} styles={styles} brandColor={brandColor} onSubmit={submitLookup} onCancel={cancelLookup} />
          )}
          {/* ONE details form (ADR-110 Am.6): the booking's and the lead's are the same component. */}
          {showBookingControls && booking.details && (
            <DetailsForm
              purpose="booking"
              ui={ui}
              styles={styles}
              brandColor={brandColor}
              fields={booking.details.fields}
              required={booking.details.required}
              language={uiLanguage}
              phoneCountry={phoneCountry}
              onSubmit={submitDetails}
              onCancel={cancelDetails}
            />
          )}
          {leadOpen && (
            <DetailsForm
              purpose="lead"
              ui={ui}
              styles={styles}
              brandColor={brandColor}
              form={leadForm}
              language={uiLanguage}
              phoneCountry={phoneCountry}
              onSubmit={handleLead}
              onCancel={() => setLeadOpen(false)}
            />
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Retry after a failed send */}
        {lastFailed && !loading && !feedbackOpen && (
          <div style={{ padding: '6px 16px 2px', display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button type="button" onClick={retry} style={styles.chip} {...chipHover} data-pwe="retry">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <RetryIcon />
                {ui.tryAgain}
              </span>
            </button>
            {statusReport && lastFailure && (
              <span
                data-pwe="report"
                data-failure={lastFailure.kind}
                style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 10px', fontSize: '12px', lineHeight: 1.4, color: '#6b7280' }}
              >
                {(reportState === 'idle' || reportState === 'failed') && (
                  <button
                    type="button"
                    onClick={reportFailure}
                    data-pwe="report-issue"
                    style={{ padding: 0, border: 'none', background: 'none', font: 'inherit', color: styles.accent, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    {ui.reportIssue}
                  </button>
                )}
                <span aria-live="polite" data-pwe="report-state">
                  {reportState === 'sending' ? '…' : reportState === 'done' ? ui.reportSent : reportState === 'failed' ? ui.reportNotSent : ''}
                </span>
                {statusReport.pageUrl && (
                  <a
                    href={statusReport.pageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-pwe="status-page"
                    style={{ color: styles.accent, textDecoration: 'underline' }}
                  >
                    {ui.statusPage}
                  </a>
                )}
              </span>
            )}
          </div>
        )}

        {/* The latest turn's booking choices (ADR-110), in place of the suggestions.
            Each sends its value verbatim under the label the visitor tapped. */}
        {showBookingControls && !feedbackOpen && booking.choices.length > 0 && (
          <div style={{ padding: '6px 16px 2px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }} data-pwe="booking-actions">
            {booking.choices.map((choice, i) => (
              <button
                key={`${i}-${choice.kind}-${choice.message}`}
                type="button"
                onClick={() => sendTurn(choice.label, { message: choice.message })}
                style={choice.kind === 'yes' ? { ...styles.chip, background: brandColor, borderColor: brandColor, color: styles.ink } : styles.chip}
                {...(choice.kind === 'yes' ? {} : chipHover)}
                data-pwe={CHOICE_PWE[choice.kind]}
              >
                {choice.label}
              </button>
            ))}
          </div>
        )}

        {/* Suggested replies (held back until the reveal finishes) */}
        {showChips && (
          <div style={{ padding: '6px 16px 2px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }} data-pwe="chips">
            <button
              type="button"
              onClick={() => {
                setSuggestions([]);
                setLinkChipHidden(true);
              }}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0,0,0,0.06)',
                color: '#6b7280',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0,
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.06)';
              }}
              aria-label={ui.dismissSuggestions}
              title={ui.dismissSuggestions}
            >
              <CloseIcon size={10} strokeWidth={3} />
            </button>
            {chips.map((chip, i) =>
              chip.kind === 'link' ? (
                // A link, not a message: it goes somewhere. The business's own
                // site opens in place; anywhere else in a new tab.
                <a
                  key="link"
                  href={chip.url}
                  {...(isSameOrigin(chip.url) ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                  style={{ ...styles.chip, display: 'inline-block', textDecoration: 'none' }}
                  {...chipHover}
                  data-pwe="link-chip"
                >
                  {chip.label}
                </a>
              ) : (
                <button key={`${i}-${chip.text}`} type="button" onClick={() => sendText(chip.text)} style={styles.chip} {...chipHover} data-pwe="chip">
                  {chip.text}
                </button>
              ),
            )}
          </div>
        )}

        {/* The two standing calls to action (1.18.0), in a row of their own
            below the conversation. Filled in the brand and carrying an icon:
            a visitor should be able to tell them from a suggestion without
            reading either. */}
        {showPrimaryActions && (
          <div style={styles.primaryRow} data-pwe="primary-actions">
            {primaryActions.map((action) => (
              <button
                key={action}
                type="button"
                onClick={action === 'lead' ? openLeadForm : startBooking}
                disabled={loading}
                style={loading ? { ...styles.primaryChip, opacity: 0.55, cursor: 'default' } : styles.primaryChip}
                {...(loading ? {} : primaryChipHover)}
                data-pwe={action === 'lead' ? 'lead-chip' : 'booking-chip'}
              >
                {action === 'lead' ? <PersonIcon /> : <CalendarIcon />}
                {action === 'lead' ? ui.leaveDetails : ui.bookAppointment}
              </button>
            ))}
          </div>
        )}

        {/* That the assistant takes any language, said where a visitor who has
            not typed yet is looking. It goes once the conversation starts; the
            input's rotating placeholder carries it from there. */}
        {showLanguageNote && (
          <div style={styles.languageNote} data-pwe="language-note">
            <GlobeIcon />
            <span>{ui.anyLanguage}</span>
          </div>
        )}

        {/* Input bar — hidden, not unmounted, behind the feedback form, so a
            half-typed message survives it. */}
        {/* The box's count from 90 % of its limit, and — only after a paste past
            it — what is wrong (1.31.0): the browser would cut the paste at
            maxLength and say nothing. */}
        {!feedbackOpen && input.length >= counterFrom(MESSAGE_MAX) && (
          <p
            id={`${composerId}-count`}
            role={input.length > MESSAGE_MAX ? 'alert' : undefined}
            style={{ margin: '0 12px', fontSize: '11px', textAlign: 'right', color: input.length > MESSAGE_MAX ? '#b91c1c' : '#6b7280' }}
            data-pwe="input-count"
          >
            {input.length > MESSAGE_MAX
              ? fill(ui.textTooLong, { label: ui.leadMessageLabel, max: String(MESSAGE_MAX), count: String(input.length) })
              : `${input.length} / ${MESSAGE_MAX}`}
          </p>
        )}
        <form onSubmit={sendMessage} style={feedbackOpen ? { display: 'none' } : styles.inputBar}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={(e) => {
              const target = e.currentTarget;
              const whole = wholePaste(input, target.selectionStart, target.selectionEnd, e.clipboardData.getData('text'), MESSAGE_MAX);
              if (whole === null) return;
              e.preventDefault();
              setInput(whole);
            }}
            placeholder={placeholders[placeholderAt] ?? ui.typeAMessage}
            aria-label={ui.typeAMessage}
            aria-busy={loading}
            aria-invalid={input.length > MESSAGE_MAX || undefined}
            aria-describedby={input.length >= counterFrom(MESSAGE_MAX) ? `${composerId}-count` : undefined}
            maxLength={MESSAGE_MAX}
            style={styles.input}
            data-pwe="input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim() || input.length > MESSAGE_MAX}
            style={sendButtonStyle(brandColor, styles.ink, !loading && input.trim() !== '' && input.length <= MESSAGE_MAX)}
            aria-label={ui.sendMessage}
            data-pwe="send"
          >
            <SendIcon />
          </button>
        </form>
      </div>
    </>
  );
}
